/**
 * Right slide-over for completed bulk Mark as Done report: summary, grouped outcomes, and
 * expandable rows backed by a single per-request returns fetch (loaded when opening from the notice).
 */
import { observer } from "mobx-react";
import { Fragment } from "react";
import Drawer from "../../../../../components/drawer/drawer";
import DateTimeV2, {
    DATETIME_VIEW,
} from "../../../../../components/datetime-v2/datetime-v2";
import FontBody2 from "../../../../../components/fonts/font-body2/font-body2";
import FontHeading4 from "../../../../../components/fonts/font-heading4/font-heading4";
import {
    FONT_COLOR,
    FONT_VARIANT,
} from "../../../../../components/fonts/font-props";
import IconCheck from "../../../../../components/icons/icon-check/icon-check";
import IconBucketErrors from "../../../../../components/icons/icon-bucket-errors/icon-bucket-errors";
import IconError from "../../../../../components/icons/icon-error/icon-error";
import IconBucketManualActions from "../../../../../components/icons/icon-bucket-manual-actions/icon-bucket-manual-actions";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import {
    BULK_REQUEST_STATUS,
    BULK_STATUS_PANEL_BUCKET,
    IBulkNewReport,
} from "../../../../../services/api/bulk-rma-action-types";
import { useDateTimeFormattingParams } from "../../../../../hooks/use-datetime-formatting-params";
import bulkRmaActionStore from "../../stores/bulk-rma-action-store";
import rmsMixpanelStore from "../../../../../stores/rms-mixpanel-store";
import RmaListBulkStatusPanelRow from "./rma-list-bulk-status-panel-row";
import RmaListBulkStatusPanelSummary from "./rma-list-bulk-status-panel-summary";
import "./rma-list-bulk-status-panel.scss";
import FontBody1 from "../../../../../components/fonts/font-body1/font-body1";

interface IProps {
    report: IBulkNewReport;
}

function SectionBucketIcon({
    bucket,
}: {
    bucket: BULK_STATUS_PANEL_BUCKET;
}): React.ReactElement {
    switch (bucket) {
        case BULK_STATUS_PANEL_BUCKET.MARKED_AS_DONE:
            return (
                <span className="rma-list-bulk-status-panel__section-icon rma-list-bulk-status-panel__section-icon--success">
                    <IconCheck color="var(--color-semantic-success)" />
                </span>
            );
        case BULK_STATUS_PANEL_BUCKET.STILL_OPEN:
            return (
                <span className="rma-list-bulk-status-panel__section-icon rma-list-bulk-status-panel__section-icon--manual-actions">
                    <IconBucketManualActions />
                </span>
            );
        case BULK_STATUS_PANEL_BUCKET.ERROR:
            return (
                <span className="rma-list-bulk-status-panel__section-icon rma-list-bulk-status-panel__section-icon--alert">
                    <IconBucketErrors />
                </span>
            );
        case BULK_STATUS_PANEL_BUCKET.NOT_PROCESSED:
            return (
                <span className="rma-list-bulk-status-panel__section-icon rma-list-bulk-status-panel__section-icon--not-processed">
                    <IconError color="var(--color-semantic-danger)" />
                </span>
            );
    }
}

const RmaListBulkStatusPanel: React.FC<IProps> = observer(({ report }) => {
    const { getTranslation } = useDashboardTranslation();
    const scope = "dashboard-returns-page";
    const dateTimeParams = useDateTimeFormattingParams();

    const panelStore = bulkRmaActionStore.panelStore;
    const isLoadingReturns = bulkRmaActionStore.isPanelReturnsLoading;
    const summary = panelStore?.summary ?? {
        succeeded: report.counts.succeeded,
        total: report.counts.total,
        needReview: report.counts.total - report.counts.succeeded,
    };

    const statusLabelKey =
        report.status === BULK_REQUEST_STATUS.CANCELLED
            ? "BULK_STATUS_REQUEST_STATUS_CANCELLED"
            : "BULK_STATUS_REQUEST_STATUS_COMPLETED";
    const finishedAtUtcString =
        report.finished_at ?? report.last_progress_at;

    return (
        <Drawer
            closeButton={{
                onClick: () => {
                    rmsMixpanelStore.trackEvent("d&_bulk_panel_close");
                    bulkRmaActionStore.closePanel();
                },
            }}
        >
            <div className="rma-list-bulk-status-panel">
                <header className="rma-list-bulk-status-panel__header">
                    <div className="rma-list-bulk-status-panel__header-text">
                        <FontHeading4>
                            {getTranslation(
                                "BULK_STATUS_PANEL_TITLE_MARK_DONE",
                                scope
                            )}
                        </FontHeading4>
                        <div className="rma-list-bulk-status-panel__meta">
                            <FontBody2 color={FONT_COLOR.PRIMARY3_50}>
                                {getTranslation(statusLabelKey, scope)}
                            </FontBody2>
                            <FontBody2 color={FONT_COLOR.PRIMARY3_50}>
                                |
                            </FontBody2>
                            <FontBody2 color={FONT_COLOR.PRIMARY3_50}>
                                {finishedAtUtcString ? (
                                    <DateTimeV2
                                        {...dateTimeParams}
                                        utcDateString={finishedAtUtcString}
                                        dateTimeView={DATETIME_VIEW.FULL}
                                    />
                                ) : (
                                    "--"
                                )}
                            </FontBody2>
                        </div>
                    </div>
                </header>

                <p className="rma-list-bulk-status-panel__summary">
                    <RmaListBulkStatusPanelSummary summary={summary} />
                </p>

                <hr className="rma-list-bulk-status-panel__divider" />

                {isLoadingReturns && (
                    <FontBody2 color={FONT_COLOR.PRIMARY3_50}>
                        {getTranslation("BULK_STATUS_LOADING_IDS", scope)}
                    </FontBody2>
                )}

                {!isLoadingReturns && panelStore && panelStore.sections.map((section, sectionIndex) => (
                    <Fragment key={section.bucket}>
                        {sectionIndex > 0 && (
                            <hr className="rma-list-bulk-status-panel__divider" />
                        )}
                        <section className="rma-list-bulk-status-panel__section">
                            <div className="rma-list-bulk-status-panel__group-header">
                                <span className="rma-list-bulk-status-panel__group-header-left">
                                    <SectionBucketIcon bucket={section.bucket} />
                                    <FontBody1 color={FONT_COLOR.PRIMARY2}>
                                        {getTranslation(
                                            section.titleKey,
                                            scope
                                        )}
                                    </FontBody1>
                                </span>
                                <FontBody2
                                    variant={FONT_VARIANT.BOLD}
                                    color={FONT_COLOR.PRIMARY2}
                                >
                                    {getTranslation(
                                        "BULK_STATUS_GROUP_RMA_COUNT",
                                        scope,
                                        [String(section.totalCount)]
                                    )}
                                </FontBody2>
                            </div>
                            {section.rows.map((row) => (
                                <RmaListBulkStatusPanelRow
                                    key={row.rowKey}
                                    row={row}
                                />
                            ))}
                        </section>
                    </Fragment>
                ))}
            </div>
        </Drawer>
    );
});

export default RmaListBulkStatusPanel;
